import { api } from '@/lib/api';
import type { ActivityFormData, LocalizedText } from '@fame/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface Activity {
  id: string;
  projectId: string;
  internalName: string;
  description: LocalizedText;
  code: string;
  articleNumber: string;
  price: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  project?: {
    id: string;
    name: LocalizedText;
  };
}

export function useActivities(projectId?: string, includeInactive = false, enabled = true) {
  const params = new URLSearchParams();
  if (projectId) params.append('projectId', projectId);
  if (includeInactive) params.append('includeInactive', 'true');
  const queryParams = params.toString() ? `?${params.toString()}` : '';

  return useQuery({
    queryKey: ['activities', projectId, includeInactive],
    queryFn: () => api.get<Activity[]>(`/activities${queryParams}`),
    enabled,
  });
}

export function useActivity(id: string) {
  return useQuery({
    queryKey: ['activities', id],
    queryFn: () => api.get<Activity>(`/activities/${id}`),
    enabled: !!id,
  });
}

export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ActivityFormData) => api.post<Activity>('/activities', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Activiteit aangemaakt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij aanmaken activiteit: ${error.message}`);
    },
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ActivityFormData> }) =>
      api.put<Activity>(`/activities/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activities', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Activiteit bijgewerkt');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij bijwerken activiteit: ${error.message}`);
    },
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/activities/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Activiteit verwijderd');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij verwijderen activiteit: ${error.message}`);
    },
  });
}
